/**
 * useWidgetLayout — the React skin over the pure tiled-grid reducer.
 *
 * Exactly the shape `usePanelSurface` has for floating panes, for the other
 * layout model: hold one grid's layout, persist EVERY change, and re-read when
 * the user id changes under a mounted component. All the rules live in
 * `./widgetLayout`; this file only turns them into state.
 *
 * THREE THINGS IT COPIES DELIBERATELY, because they were each a bug once:
 *
 *   • MUTATORS TAKE A REDUCER, NOT A VALUE. Two ops in one handler must
 *     compose — computing both from the `layout` this render captured makes
 *     the second silently discard the first.
 *   • EVERY CHANGE IS WRITTEN. There is no save-on-exit: leaving edit mode
 *     only flips a flag, because by then the layout has been on disk since the
 *     drag ended.
 *   • THE USER IS PART OF THE KEY. A first-ever login starts on `anon` and the
 *     real id arrives with the profile; without the reload below the grid
 *     would keep the anonymous arrangement and then save it under the real
 *     user's key.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLayoutUserId from '../../services/layout/useLayoutUserId';
import useLayoutSync from '../../services/layout/useLayoutSync';
import {
  WidgetGrid,
  WidgetHeight,
  WidgetLayout,
  clearWidgetLayout,
  loadWidgetLayout,
  moveWidget,
  moveWidgetBy,
  resetLayout,
  saveWidgetLayout,
  setWidgetHeight,
  validateWidgetLayout,
  widgetLayoutKey,
  setWidgetSpan,
  setWidgetVisible,
  toggleWidget,
} from './widgetLayout';

export interface UseWidgetLayoutResult<Id extends string> {
  layout: WidgetLayout<Id>;
  /** Whose arrangement this is — the storage key's user segment. */
  userId: string;
  setVisible: (id: Id, visible: boolean) => void;
  toggle: (id: Id) => void;
  setSpan: (id: Id, span: number) => void;
  setHeight: (id: Id, height: WidgetHeight) => void;
  /** Drop `id` in front of whatever sits at (row, col). */
  move: (id: Id, row: number, col: number) => void;
  /**
   * Step `id` one place earlier (-1) or later (+1) — the keyboard's drag.
   *
   * `allow` is the caller's render gate, so the step skips widgets that are
   * not on screen this paint rather than swapping with one of them.
   */
  moveBy: (id: Id, delta: number, allow?: (id: Id) => boolean) => void;
  reset: () => void;
}

export function useWidgetLayout<Id extends string>(
  grid: WidgetGrid<Id>
): UseWidgetLayoutResult<Id> {
  const userId = useLayoutUserId();
  const [layout, setLayout] = useState<WidgetLayout<Id>>(() =>
    loadWidgetLayout(grid, userId)
  );

  /*
    THE SERVER'S COPY, once it arrives.

    The cache above is what paints; this is what makes the arrangement follow
    the user to another machine. `validateWidgetLayout` runs over it for the
    same reason it runs over storage — a payload is untrusted input, and one
    written by an older build may name a widget that has since been retired.
  */
  const storageKey = useMemo(() => widgetLayoutKey(grid, userId), [grid, userId]);
  useLayoutSync(storageKey, (raw) => setLayout(validateWidgetLayout(grid, raw)));

  const loaded = useRef({ grid, userId });
  useEffect(() => {
    if (loaded.current.grid === grid && loaded.current.userId === userId) return;
    loaded.current = { grid, userId };
    setLayout(loadWidgetLayout(grid, userId));
  }, [grid, userId]);

  const apply = useCallback(
    (change: (current: WidgetLayout<Id>) => WidgetLayout<Id>) => {
      setLayout((current) => {
        const next = change(current);
        // An op that changed nothing (a span already at its floor, a height
        // re-picked) must not write, or a no-op drag would touch storage.
        if (next === current) return current;
        saveWidgetLayout(grid, next, userId);
        return next;
      });
    },
    [grid, userId]
  );

  return {
    layout,
    userId,
    setVisible: useCallback(
      (id: Id, visible: boolean) => apply((l) => setWidgetVisible(grid, l, id, visible)),
      [grid, apply]
    ),
    toggle: useCallback((id: Id) => apply((l) => toggleWidget(grid, l, id)), [grid, apply]),
    setSpan: useCallback(
      (id: Id, span: number) => apply((l) => setWidgetSpan(grid, l, id, span)),
      [grid, apply]
    ),
    setHeight: useCallback(
      (id: Id, height: WidgetHeight) => apply((l) => setWidgetHeight(grid, l, id, height)),
      [grid, apply]
    ),
    move: useCallback(
      (id: Id, row: number, col: number) => apply((l) => moveWidget(grid, l, id, row, col)),
      [grid, apply]
    ),
    moveBy: useCallback(
      (id: Id, delta: number, allow?: (id: Id) => boolean) =>
        apply((l) => moveWidgetBy(grid, l, id, delta, { allow })),
      [grid, apply]
    ),
    /*
      Reset FORGETS rather than writes.

      It used to save the defaults over the top, which was indistinguishable
      from a stored layout that happened to match them. Now that a scope lives
      on the server too, the difference matters: clearing removes the row
      upstream as well, so the next load — here or on another device — falls
      through to whatever the built-in default is THEN, rather than to a
      snapshot of what it was on the day someone pressed the button.
    */
    reset: useCallback(() => {
      clearWidgetLayout(grid, userId);
      setLayout(resetLayout(grid));
    }, [grid, userId]),
  };
}

export default useWidgetLayout;
